import {
  NotificationBannerItem,
  RedoNotificationBanner,
} from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { filterTruthy } from "@redotech/util/array";
import { memo, useMemo } from "react";
import { useFedexMfaBanner } from "./fedex-mfa-banner";
import { useFixDeveloperWebhooks } from "./fix-developer-webhooks";
import { useLogInToRedo } from "./log-in-to-redo";
import { useNewPermissions } from "./new-permissions";
import { useRevokedEmailPermissions } from "./revoked-email-permissions";
import { useShopifyBillingNotification } from "./shopify-billing";
import { useTeamDeletion } from "./team-deletion";
import { useWalmartOmsReauthorization } from "./walmart-oms-reauthorization";

/**
 * Active Action -- some ongoing process, will get removed once action is taken
 * Critical -- your team will be deleted
 * High -- most if not all of redo will not work
 * Medium -- some features of redo will not work
 * Low -- minor inconvenience, does not affect core functionality
 */
export enum ActionRequiredSeverity {
  ActiveAction = "active_action",
  Critical = "critical",
  High = "high",
  Medium = "medium",
  Low = "low",
}

const severitySort = (a: ActionRequiredSeverity, b: ActionRequiredSeverity) => {
  const order = [
    ActionRequiredSeverity.ActiveAction,
    ActionRequiredSeverity.Critical,
    ActionRequiredSeverity.High,
    ActionRequiredSeverity.Medium,
    ActionRequiredSeverity.Low,
  ];

  return order.indexOf(a) - order.indexOf(b);
};

export const ActionRequiredBanner = memo(function ActionRequiredBanner() {
  const shopifyBilling = useShopifyBillingNotification();
  const webhookBanner = useFixDeveloperWebhooks();
  const logInToRedo = useLogInToRedo();
  const newPermissions = useNewPermissions();
  const revokedEmailPermissions = useRevokedEmailPermissions();
  const teamDeletion = useTeamDeletion();
  const walmartOmsReauthorization = useWalmartOmsReauthorization();
  const fedexMfaBanner = useFedexMfaBanner();

  const banners: NotificationBannerItem[] = useMemo(
    () =>
      filterTruthy([
        ...(shopifyBilling || []),
        webhookBanner,
        logInToRedo,
        newPermissions,
        revokedEmailPermissions,
        teamDeletion,
        walmartOmsReauthorization,
        fedexMfaBanner,
      ])
        .sort((a, b) => severitySort(a.severity, b.severity))
        .map((banner) => banner.item),
    [
      webhookBanner,
      logInToRedo,
      newPermissions,
      revokedEmailPermissions,
      teamDeletion,
      walmartOmsReauthorization,
      shopifyBilling,
      fedexMfaBanner,
    ],
  );

  return <RedoNotificationBanner items={banners} />;
});
